import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { settingsApi } from '../shared/api/endpoints';
import TransparentLogo from '../shared/components/TransparentLogo';
import { mediaUrl, resolveSiteMedia } from '../shared/utils/mediaUrl';
import defaultLogo from '../shared/assets/logo.png';
import './PeacockIntro.css';

/**
 * Site open intro:
 * - Admin video (local /assets/intro) → play then fade out
 * - Admin / default logo → bg removed, scale + erase
 */
export default function PeacockIntro({ onComplete }) {
  const [settings, setSettings] = useState(null);
  const [ready, setReady] = useState(false);
  const [fading, setFading] = useState(false);
  const doneRef = useRef(false);
  const videoRef = useRef(null);

  useEffect(() => {
    settingsApi
      .get()
      .then((res) => setSettings(res.data.data || null))
      .catch(() => setSettings(null))
      .finally(() => setReady(true));
  }, []);

  const finish = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    setFading(true);
    setTimeout(() => onComplete?.(), 900);
  };

  const useVideo = settings?.introMode === 'video' && (settings?.introVideo || false);
  const logoSrc = resolveSiteMedia(settings, 'introLogo') || resolveSiteMedia(settings, 'brandLogo') || defaultLogo;
  const videoSrc = settings?.introVideo ? mediaUrl(settings.introVideo) : '';

  useEffect(() => {
    if (!ready || useVideo) return undefined;
    const timer = setTimeout(finish, 2800);
    return () => clearTimeout(timer);
  }, [ready, useVideo]);

  useEffect(() => {
    if (!ready || !useVideo || !videoSrc) return undefined;
    const timer = setTimeout(finish, 12000);
    return () => clearTimeout(timer);
  }, [ready, useVideo, videoSrc]);

  if (!ready) {
    return <div className="peacock-intro peacock-intro--loading" />;
  }

  return (
    <motion.div
      className={`peacock-intro ${fading ? 'peacock-intro--fade' : ''}`}
      initial={{ opacity: 1 }}
      animate={{ opacity: fading ? 0 : 1 }}
      transition={{ duration: 0.9, ease: 'easeInOut' }}
    >
      <div className="peacock-intro__glow" />

      {useVideo && videoSrc ? (
        <video
          ref={videoRef}
          className="peacock-intro__video"
          src={videoSrc}
          autoPlay
          muted
          playsInline
          onEnded={finish}
          onError={finish}
        />
      ) : (
        <TransparentLogo
          src={logoSrc}
          alt="NAYAVARNAM"
          className="peacock-intro__logo"
          asMotion
          MotionComponent={motion.img}
          motionProps={{
            initial: { scale: 0.72, opacity: 0.95 },
            animate: { scale: 1.22, opacity: fading ? 0 : 1 },
            transition: { duration: 2.6, ease: [0.22, 1, 0.36, 1] },
          }}
        />
      )}
    </motion.div>
  );
}
